import React, { useState, useEffect } from 'react';
import { getProductImage, handleImageError } from '../utils/imageUtils';

/**
 * ProductImage - Reusable component for displaying product images with automatic fallback
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - Additional CSS classes
 * @param {Object} style - Inline styles
 * @param {Function} onError - Custom error handler (optional)
 * @param {Object} props - Other img props
 */
const ProductImage = ({ 
  src, 
  alt = 'Product', 
  className = '', 
  style = {},
  onError,
  decoding = 'async',
  loading = 'lazy',
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(() => getProductImage(src));
  const [hasError, setHasError] = useState(false);

  // Sync displayed image when src prop changes (e.g. PDP gallery thumbnail click)
  useEffect(() => {
    const nextSrc = getProductImage(src);
    setImageSrc(nextSrc);
    setHasError(false);
  }, [src]);

  const handleError = (event) => {
    if (!hasError) {
      setHasError(true);
      setImageSrc(getProductImage(null));
      
      // Call custom error handler if provided
      if (onError) {
        onError(event);
      } else {
        // Use default error handler
        handleImageError(event);
      }
    }
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onError={handleError}
      loading={loading}
      decoding={decoding}
      {...props}
    />
  );
};

export default ProductImage;
